import React, { useEffect, useState } from 'react';
import { getLanguageForExperience, getTranslations } from './constants/natalTranslations';
import { updateMetaTags } from './utils/metaTagUpdater';

const NatalPage = () => {
  const [clientName, setClientName] = useState('');
  const [experienceData, setExperienceData] = useState(null);
  const [startClicked, setStartClicked] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [language, setLanguage] = useState('pt');
  const [translations, setTranslations] = useState(getTranslations('pt'));

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const experience = searchParams.get('experience') || 'sintra';
    const name = searchParams.get('name');

    // Set language based on experience
    const lang = getLanguageForExperience(experience);
    setLanguage(lang);
    const trans = getTranslations(lang);
    setTranslations(trans);

    // Update meta tags based on experience
    if (experience === 'xmas') {
      updateMetaTags({
        title: 'Sofia\'s Christmas 🎄 - Interactive Game',
        description: 'Help Sofia discover the truth about Christmas in Portugal! Test your knowledge with this fun, interactive game. 🍷✨',
        image: 'https://firebasestorage.googleapis.com/v0/b/guidesofiavirtual360.firebasestorage.app/o/natal%2Fsofia1-removebg-preview.png?alt=media',
        url: window.location.href,
      });
    } else {
      updateMetaTags({
        title: `O Natal da Sofia - ${experience.charAt(0).toUpperCase() + experience.slice(1)}`,
        description: 'Explore Portugal\'s Christmas traditions with Sofia, your AI audio guide.',
        image: 'https://www.guidesofia.com/wp-content/uploads/2025/05/Circle-logo.png',
        url: window.location.href,
      });
    }

    const jsonUrl = `/natal/experiences/${experience}.json`;
    loadExperienceData(jsonUrl, name);

    createSnow();
  }, []);

  const loadExperienceData = async (url, urlName) => {
    try {
      const response = await fetch(url);
      const data = await response.json();
      setExperienceData(data.experience_data);

      if (urlName) {
        setClientName(urlName);
      } else {
        setClientName(data.experience_data.vip_name_placeholder || 'Viajante');
      }
    } catch (error) {
      console.error('Error loading experience data:', error);
    }
  };

  const createSnow = () => {
    const container = document.body;
    for (let i = 0; i < 40; i++) {
      const snow = document.createElement('div');
      snow.textContent = '❄';
      snow.className = 'snowflake';
      snow.style.left = Math.random() * 100 + 'vw';
      snow.style.animationDuration = Math.random() * 3 + 5 + 's';
      snow.style.opacity = Math.random().toString();
      snow.style.fontSize = Math.random() * 10 + 10 + 'px';
      snow.style.transform = `translateX(${Math.random() * 20}px)`;

      container.appendChild(snow);

      snow.addEventListener('animationend', () => {
        snow.remove();
        createSnowSingle();
      });
    }
  };

  const createSnowSingle = () => {
    const snow = document.createElement('div');
    snow.textContent = '❄';
    snow.className = 'snowflake';
    snow.style.left = Math.random() * 100 + 'vw';
    snow.style.animationDuration = Math.random() * 3 + 5 + 's';
    snow.style.opacity = Math.random().toString();
    snow.style.fontSize = Math.random() * 10 + 10 + 'px';
    document.body.appendChild(snow);
    snow.addEventListener('animationend', () => {
      snow.remove();
      createSnowSingle();
    });
  };

  const generateBars = () => {
    const containers = document.querySelectorAll('.audio-wave-container');
    containers.forEach((c) => {
      c.replaceChildren();
      for (let i = 0; i < 15; i++) {
        const bar = document.createElement('div');
        bar.className = 'bar';
        bar.style.animationDelay = Math.random() * 0.5 + 's';
        bar.style.height = Math.random() * 60 + 20 + '%';
        c.appendChild(bar);
      }
    });
  };

  const stopAllAudio = () => {
    const audioElements = document.querySelectorAll('audio');
    audioElements.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    document.querySelectorAll('.play-btn-circle').forEach((btn) => {
      btn.textContent = '▶';
    });
    document.querySelectorAll('.audio-player-ui').forEach((ui) => {
      ui.classList.remove('playing');
    });
  };

  const startExperience = () => {
    stopAllAudio();
    document.getElementById('intro')?.classList.remove('active');
    document.getElementById('level-1')?.classList.add('active');
    setTimeout(() => {
      generateBars();
      playAudio('level-1');
    }, 100);
  };

  const playAudio = (id) => {
    const audioId = typeof id === 'string' ? id : `level-${id}`;
    const playerUi = document.querySelector(`#player-${audioId}`);
    const btn = document.querySelector(`#player-${audioId} .play-btn-circle`);
    const audioElement = document.getElementById(`audio-${audioId}`);

    if (playerUi && btn && audioElement) {
      playerUi.classList.add('playing');
      btn.textContent = '❚❚';

      const playPromise = audioElement.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => console.log('Audio autoplay failed:', error));
      }

      audioElement.onended = () => {
        playerUi.classList.remove('playing');
        btn.textContent = '▶';
      };
    }
  };

  const togglePlay = (id) => {
    const audioId = typeof id === 'string' ? id : `level-${id}`;
    const playerUi = document.querySelector(`#player-${audioId}`);
    const btn = document.querySelector(`#player-${audioId} .play-btn-circle`);
    const audioElement = document.getElementById(`audio-${audioId}`);

    if (playerUi && btn && audioElement) {
      if (audioElement.paused) {
        stopAllAudio();
        audioElement.play();
        playerUi.classList.add('playing');
        btn.textContent = '❚❚';
      } else {
        audioElement.pause();
        playerUi.classList.remove('playing');
        btn.textContent = '▶';
      }
    }
  };

  const checkAnswer = (btn, isCorrect, level) => {
    if (isCorrect) {
      btn.classList.add('correct');
      btn.textContent = `✓ ${btn.textContent}`;

      setTimeout(() => {
        stopAllAudio();
        if (level === 1) {
          document.getElementById('level-1')?.classList.remove('active');
          document.getElementById('level-2')?.classList.add('active');
          setTimeout(() => {
            generateBars();
            setTimeout(() => playAudio('level-2'), 50);
          }, 100);
        } else if (level === 2) {
          document.getElementById('level-2')?.classList.remove('active');
          document.getElementById('level-3')?.classList.add('active');
          setTimeout(() => {
            generateBars();
            setTimeout(() => playAudio('level-3'), 50);
          }, 100);
        } else {
          document.getElementById('level-3')?.classList.remove('active');
          document.getElementById('final')?.classList.add('active');
          setTimeout(() => {
            generateBars();
            setTimeout(() => playAudio('outro'), 50);
          }, 100);
        }
      }, 1200);
    } else {
      btn.classList.add('wrong');
      const originalText = btn.textContent;
      btn.textContent = `${translations.trueStatement} ${translations.tryAgain}`;
      setTimeout(() => {
        btn.classList.remove('wrong');
        btn.textContent = originalText;
      }, 1500);
    }
  };

  const shareGame = () => {
    if (!recipientName.trim()) {
      alert(translations.sharePrompt);
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const experience = searchParams.get('experience') || 'sintra';
    const baseUrl = window.location.href.split('?')[0];
    const personalizedUrl = `${baseUrl}?experience=${experience}&name=${encodeURIComponent(recipientName)}`;

    if (navigator.share) {
      navigator.share({
        title: translations.shareTitle,
        text: translations.shareText(recipientName),
        url: personalizedUrl,
      }).catch(() => {
        alert(translations.shareFallbackText + personalizedUrl);
      });
    } else {
      alert(translations.shareFallbackText + personalizedUrl);
    }
  };

  const handleStartClick = () => {
    setStartClicked(true);
    setTimeout(() => {
      generateBars();
      setTimeout(() => {
        playAudio('intro');
      }, 100);
    }, 100);
  };

  if (!experienceData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f171e' }}>
        <p style={{ color: 'white', fontSize: '18px' }}>Loading...</p>
      </div>
    );
  }

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Lato:wght@400;700&display=swap" rel="stylesheet" />

      <style>{`
        :root {
          --bg-deep: #0f171e;
          --card-bg: #fffbf0;
          --brand-blue: #4eb5cd;
          --xmas-red: #c0392b;
          --xmas-gold: #d4af37;
          --text-dark: #2c3e50;
          --success-green: #27ae60;
        }

        body {
          margin: 0;
          font-family: 'Lato', sans-serif;
          background-color: var(--bg-deep);
          background-image: url('/natal/bgx.jpg');
          background-repeat: repeat;
          background-size: 25%;
          background-attachment: fixed;
          color: var(--text-dark);
          min-height: 100vh;
          overflow-x: hidden;
          display: flex;
          flex-direction: column;
        }

        .game-container {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            width: 100%;
            perspective: 1000px;
        }

        .postcard {
          background-color: var(--card-bg);
          background-image: url("https://www.transparenttextures.com/patterns/cream-paper.png");
          border-radius: 12px;
          width: 100%;
          max-width: 450px;
          padding: 30px 25px;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6);
          position: relative;
          text-align: center;
          margin: 20px auto;
          animation: slideUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

          border: 8px solid transparent;
          border-image: repeating-linear-gradient(
              45deg,
              var(--xmas-gold),
              var(--xmas-gold) 10px,
              var(--xmas-red) 10px,
              var(--xmas-red) 20px,
              var(--card-bg) 20px,
              var(--card-bg) 25px
          ) 1;
          outline: 2px solid var(--xmas-gold);
          outline-offset: -12px;
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px) rotate(2deg); }
          to { opacity: 1; transform: translateY(0) rotate(0); }
        }

        .stamp {
            position: absolute;
            top: 20px;
            right: 20px;
            width: 55px;
            height: 65px;
            border: 2px dotted var(--xmas-red);
            opacity: 0.6;
            transform: rotate(10deg);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-family: 'Courier New', monospace;
            font-size: 9px;
            color: var(--xmas-red);
            z-index: 5;
        }
        .stamp span { font-size: 18px; font-weight: bold; }

        h1.greeting {
            font-family: 'Great Vibes', cursive;
            color: var(--xmas-red);
            font-size: 3.2rem;
            margin: 10px 0 5px 0;
            line-height: 1.1;
            transform: rotate(-3deg);
            text-shadow: 2px 2px 0px rgba(0,0,0,0.1);
            z-index: 10;
            position: relative;
        }

        h2.subtitle {
            font-family: 'Lato', sans-serif;
            text-transform: uppercase;
            letter-spacing: 3px;
            font-size: 0.75rem;
            color: var(--brand-blue);
            margin-top: 0;
            margin-bottom: 20px;
            font-weight: 700;
        }

        h2.level-title {
           font-family: 'Lato', sans-serif;
           color: var(--text-dark);
           font-size: 1.2rem;
           margin-bottom: 15px;
        }

        p.context {
            font-family: 'Lato', sans-serif;
            font-size: 1.05rem;
            line-height: 1.5;
            color: #444;
            margin-bottom: 25px;
        }

        .mascot-full {
            display: block;
            margin: 0 auto;
            height: 280px;
            width: auto;
            object-fit: contain;
            filter: drop-shadow(0 10px 10px rgba(0,0,0,0.3));
            margin-top: -10px;
            margin-bottom: 10px;
            position: relative;
            z-index: 2;
            animation: float 4s ease-in-out infinite;
        }

        .mascot-head {
            width: 80px;
            height: 80px;
            object-fit: cover;
            border-radius: 50%;
            border: 3px solid white;
            box-shadow: 0 4px 10px rgba(0,0,0,0.1);
            margin: -60px auto 10px auto;
            background: white;
            position: relative;
            z-index: 2;
        }

        @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-5px); }
            100% { transform: translateY(0px); }
        }

        .audio-player-ui {
          background: transparent;
          border: 1px solid var(--xmas-gold);
          border-radius: 50px;
          padding: 8px 15px;
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 25px;
          box-shadow: none;
        }

        .play-btn-circle {
          width: 40px;
          height: 40px;
          background: var(--xmas-red);
          border-radius: 50%;
          border: none;
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: transform 0.2s;
          flex-shrink: 0;
        }

        .play-btn-circle:hover { transform: scale(1.05); }

        .audio-wave-container {
          flex: 1;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
        }

        .bar {
            width: 3px;
            background-color: #ddd;
            border-radius: 2px;
            height: 20%;
        }

        .playing .bar {
            background-color: var(--brand-blue);
            animation: sound 0.5s linear infinite alternate;
        }

        @keyframes sound {
            0% { height: 20%; }
            100% { height: 100%; }
        }

        .cta-button {
            background: linear-gradient(135deg, var(--brand-blue), #3a8d9f);
            color: white;
            border: none;
            padding: 16px 32px;
            border-radius: 50px;
            font-size: 1.1rem;
            font-weight: bold;
            cursor: pointer;
            width: 100%;
            box-shadow: 0 8px 20px rgba(78, 181, 205, 0.4);
            transition: transform 0.2s;
            font-family: 'Lato', sans-serif;
            text-transform: uppercase;
            letter-spacing: 1px;
            border: 2px solid white;
            margin-bottom: 10px;
        }

        .cta-button:hover {
             transform: translateY(-2px);
             background: linear-gradient(135deg, #5ac6e0, #4eb5cd);
        }

        .secondary-button {
          background: transparent;
          color: var(--brand-blue);
          border: 2px solid var(--brand-blue);
          padding: 12px 32px;
          border-radius: 30px;
          font-size: 0.9rem;
          font-weight: 700;
          cursor: pointer;
          width: 100%;
          margin-top: 10px;
        }

        .choice-btn {
          background: white;
          border: 2px solid #eee;
          border-radius: 12px;
          padding: 15px;
          width: 100%;
          margin-bottom: 12px;
          text-align: left;
          font-family: 'Lato', sans-serif;
          font-size: 1rem;
          color: var(--text-dark);
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }

        .choice-btn:hover {
          border-color: var(--brand-blue);
          background: #f0fbff;
        }

        .choice-btn.correct {
          background-color: #eafaf1;
          border-color: var(--success-green);
          color: #1e8e4a;
          font-weight: bold;
        }

        .choice-btn.wrong {
          background-color: #fdedec;
          border-color: var(--xmas-red);
          color: #c0392b;
        }

        .share-module {
          border: 2px solid #ccc;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 20px;
          transition: all 0.2s ease;
          background: white;
        }

        .share-module:focus-within {
          border-color: var(--xmas-gold);
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.2);
        }

        .share-input-container {
          margin-bottom: 15px;
        }

        .share-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid var(--brand-blue);
          border-radius: 30px;
          font-family: 'Lato', sans-serif;
          font-size: 1rem;
          text-align: center;
          color: var(--text-dark);
          background: white;
          transition: all 0.2s;
        }

        .share-input:focus {
          outline: none;
          border-color: var(--xmas-gold);
          box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
        }

        .share-input::placeholder {
          color: #999;
        }

        .play-again-link {
          display: block;
          margin-top: 20px;
          text-align: center;
          color: var(--brand-blue);
          text-decoration: none;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s ease;
          background: none;
          border: none;
          font-family: 'Lato', sans-serif;
          padding: 0;
        }

        .play-again-link:hover {
          color: var(--xmas-red);
          text-decoration: underline;
        }

        .snowflake {
          position: fixed;
          top: -10px;
          color: #fff;
          font-size: 1em;
          text-shadow: 0 0 4px rgba(255,255,255,0.8);
          z-index: 1;
          animation: fall linear forwards;
          pointer-events: none;
        }

        @keyframes fall {
          to { transform: translateY(105vh); }
        }

        .screen { display: none; }
        .screen.active { display: block; }

        header {
            background: white;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            position: sticky;
            top: 0;
            z-index: 50;
            border-bottom: 1px solid #eee;
            padding: 12px 16px;
        }

        header > div {
            max-width: 1280px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: space-between;
        }

        header a img {
            height: 40px;
            width: auto;
            object-fit: contain;
        }

        .header-menu-container ul {
            display: flex;
            gap: 0.5rem;
            align-items: flex-start;
            margin: 0;
            padding: 0;
            list-style: none;
        }

        .header-menu-container {
            margin-left: 2rem;
        }

        .header-menu-container ul li {
            text-align: left;
        }

        .header-menu-container ul li a {
            color: #4b5563;
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.15em;
            font-weight: 600;
            transition: color 0.2s ease;
            text-decoration: none;
            display: block;
            vertical-align: top;
            line-height: 1;
        }

        .header-menu-container ul li a:hover {
            color: #00BEDD;
        }

        @media (max-width: 768px) {
            .header-menu-container ul {
                gap: 0.75rem;
            }

            .header-menu-container ul li a {
                font-size: 0.65rem;
            }

            body {
                background-size: 100%;
            }
        }
      `}</style>

      <header>
          <div>
              <a href="/" style={{display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0}}>
                  <img src="https://www.guidesofia.com/wp-content/uploads/2025/05/Circle-logo.png" alt="GuideSofia Logo" />
              </a>

              <div className="header-menu-container">
                  <ul>
                      <li><a href="https://www.guidesofia.com/">A Sofia</a></li>
                      <li><a href="https://www.guidesofia.com/business/">For Businesses</a></li>
                      <li><a href="https://www.guidesofia.com/360-3/">360 Virtual Tours</a></li>
                      <li><a href="https://www.guidesofia.com/about-us/">About Us</a></li>
                  </ul>
              </div>
          </div>
      </header>

      <div className="game-container">
        {/* START SCREEN */}
        {!startClicked && experienceData && (
          <div id="start" className="screen active">
            <div className="postcard">
              <div className="stamp">
                <span>25</span>
                DEZ
              </div>
              <h1 className="greeting">{translations.welcomeGreeting(clientName || experienceData.vip_name_placeholder)}</h1>
              <h2 className="subtitle">{translations.welcomeSubtitle}</h2>
              <img src="https://firebasestorage.googleapis.com/v0/b/guidesofiavirtual360.firebasestorage.app/o/natal%2Fsofia1-removebg-preview.png?alt=media&token=d618decb-4df7-452a-b1a6-73f2d2b12fb3" alt="Sofia Full Body" className="mascot-full" />
              <p className="context">{translations.welcomeDescription}</p>
              <button className="cta-button" onClick={handleStartClick}>
                {translations.startButton}
              </button>
            </div>
          </div>
        )}

        {/* INTRO SCREEN */}
        {startClicked && experienceData && (
          <div id="intro" className="screen active">
            <div className="postcard">
              <div className="stamp"><span>00</span>INTRO</div>
              <img src="https://firebasestorage.googleapis.com/v0/b/guidesofiavirtual360.firebasestorage.app/o/natal%2Fsofia_face-removebg-preview.png?alt=media&token=c594728f-972b-474e-baed-6034774b856d" alt="Sofia Face" className="mascot-head" />
              <h2 className="level-title" style={{color: 'var(--xmas-red)', fontFamily: 'Great Vibes', fontSize: '2.5rem'}}>
                {translations.missionTitle}
              </h2>
              <div className="audio-player-ui" id="player-intro">
                <button className="play-btn-circle" onClick={() => togglePlay('intro')} type="button">▶</button>
                <div className="audio-wave-container" id="wave-intro"></div>
              </div>
              <audio id="audio-intro" src={experienceData.intro_section.audio_url} style={{display: 'none'}} />

              <button className="cta-button" onClick={startExperience}>
                {translations.introButton}
              </button>

              {/* Webview 360 Tour */}
              {experienceData.intro_section.virtual_tour_url && (
                <div style={{width: '100%', aspectRatio: '3/4', marginBottom: '20px', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', border: '2px solid var(--brand-blue)'}}>
                  <iframe
                    src={experienceData.intro_section.virtual_tour_url}
                    style={{width: '100%', height: '100%', border: 'none'}}
                    title="Virtual 360 Tour"
                    allowFullScreen={true}
                  />
                </div>
              )}

              <p className="context" style={{fontSize: '0.95rem'}}>{experienceData.intro_section.text_script}</p>
            </div>
          </div>
        )}

        {/* LEVEL SCREENS */}
        {experienceData?.scenarios.map((scenario, index) => (
          <div id={`level-${index + 1}`} key={scenario.id} className="screen">
            <div className="postcard">
              <div className="stamp"><span>0{index + 1}</span>LVL</div>
              <img src="https://firebasestorage.googleapis.com/v0/b/guidesofiavirtual360.firebasestorage.app/o/natal%2Fsofia_face-removebg-preview.png?alt=media&token=c594728f-972b-474e-baed-6034774b856d" alt="Sofia Face" className="mascot-head" />
              <h2 className="level-title">{scenario.monument_name}</h2>
              <div className="audio-player-ui" id={`player-level-${index + 1}`}>
                <button className="play-btn-circle" onClick={() => togglePlay(`level-${index + 1}`)} type="button">▶</button>
                <div className="audio-wave-container" id={`wave-level-${index + 1}`}></div>
              </div>
              <audio id={`audio-level-${index + 1}`} src={scenario.audio_url} style={{display: 'none'}} />
              <p className="context" style={{marginBottom: '10px'}}>{translations.listen}</p>
              <p style={{fontSize: '0.85rem', color: 'var(--xmas-red)', marginBottom: '15px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px'}}>
                {translations.whichIsLie}
              </p>
              {scenario.options.map((option, optionIndex) => (
                <button key={optionIndex} className="choice-btn" onClick={(e) => checkAnswer(e.currentTarget, option.is_correct_answer, index + 1)}>
                  {option.button_text}
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* FINAL SCREEN */}
        {experienceData && (
          <div id="final" className="screen">
            <div className="postcard" style={{borderImage: 'none', border: '4px double var(--xmas-gold)'}}>
              <img src="/images/sofia_final.png" alt="Sofia Final" className="mascot-full" style={{height: '200px', marginBottom: '20px'}} />
              <h1 className="greeting" style={{transform: 'rotate(0)'}}>
                {translations.missionComplete}
              </h1>
              <div className="audio-player-ui" id="player-outro">
                <button className="play-btn-circle" onClick={() => togglePlay('outro')} type="button">▶</button>
                <div className="audio-wave-container" id="wave-outro"></div>
              </div>
              <audio id="audio-outro" src={experienceData.outro_section.audio_url} style={{display: 'none'}} />
              <p className="context" style={{fontSize: '0.9rem', marginBottom: '20px'}}>
                {translations.shareMessage}
              </p>
              <div className="share-module">
                <div className="share-input-container">
                  <input
                    type="text"
                    className="share-input"
                    placeholder={translations.shareInputPlaceholder}
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                  />
                </div>
                <button className="cta-button" onClick={shareGame} style={{background: 'var(--xmas-red)'}}>
                  {translations.shareButton}
                </button>
              </div>

              {/* App Download Section - Only show on English version */}
              {language === 'en' && (
                <div style={{marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #eee', textAlign: 'center'}}>
                  <h3 style={{color: 'var(--text-dark)', fontSize: '1.1rem', marginBottom: '10px'}}>
                    {translations.downloadAppTitle}
                  </h3>
                  <p style={{color: '#666', fontSize: '0.9rem', marginBottom: '20px', lineHeight: '1.5'}}>
                    {translations.downloadAppText}
                  </p>

                  <div style={{display: 'flex', justifyContent: 'center', gap: '30px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '20px'}}>
                    {/* QR Code */}
                    <div style={{textAlign: 'center'}}>
                      <p style={{fontSize: '0.8rem', color: '#999', marginBottom: '8px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                        Scan to Download
                      </p>
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(translations.downloadAppWebsite)}`}
                        alt="Download GuideSofia App QR Code"
                        style={{border: '2px solid var(--brand-blue)', padding: '8px', backgroundColor: 'white', borderRadius: '8px'}}
                      />
                    </div>

                    {/* Direct Link */}
                    <div>
                      <a
                        href={translations.downloadAppWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, var(--brand-blue), #3a8d9f)',
                          color: 'white',
                          padding: '14px 28px',
                          borderRadius: '50px',
                          textDecoration: 'none',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          border: '2px solid white',
                          boxShadow: '0 8px 20px rgba(78, 181, 205, 0.4)',
                          transition: 'transform 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                      >
                        {translations.downloadAppButton}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              <button className="play-again-link" onClick={() => window.location.reload()}>
                {translations.playAgainLink}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NatalPage;
